/**
 * useSessions hook
 * React Query hook for fetching and managing session data
 * Provides filtered arrays for active and completed sessions
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getSessions } from '@/services/sessions';
import type { Session } from '@/types/session';

/**
 * Return type for useSessions hook
 */
export interface UseSessionsReturn {
  /** All sessions (unfiltered) */
  sessions: Session[] | undefined;

  /** Active sessions only, sorted by most recent first */
  activeSessions: Session[];

  /** Completed sessions only, sorted by most recent first */
  completedSessions: Session[];

  /** Loading state - true during initial fetch */
  isLoading: boolean;

  /** Error object if fetch failed */
  error: Error | null;

  /** Function to manually refetch sessions */
  refetch: () => void;
}

/**
 * Hook to fetch and manage sessions data with React Query
 * Automatically filters sessions by status and sorts by most recent
 *
 * @returns Object containing sessions data, loading state, error, and refetch function
 */
export function useSessions(): UseSessionsReturn {
  const {
    data: sessions,
    isLoading,
    error,
    refetch,
  } = useQuery<Session[], Error>({
    queryKey: ['sessions'],
    queryFn: () => getSessions(),
    staleTime: 30000, // 30 seconds to reduce unnecessary refetches
  });

  // Filter and sort active sessions (most recent first)
  // Implements TS-018 T4 (CCM-01 reader): status === 'active' correctly EXCLUDES
  // 'cancelled' sessions — a cancelled session must not appear in the active list
  // and must not block a new session start. No change needed here; comment added
  // for CCM-01 audit trail.
  const activeSessions = useMemo(() => {
    if (!sessions) return [];

    return sessions
      .filter((session) => session.status === 'active')
      .sort((a, b) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [sessions]);

  // Filter and sort terminal sessions (completed OR cancelled OR ended_by_parent) for
  // history display.
  // Implements TS-018 T4 (CCM-01 reader): previously only 'completed' was included,
  // which silently dropped 'cancelled' sessions from session history. Per US-018 /
  // CCM-01, cancelled sessions must remain visible in history so families can see
  // their Report Issue submissions. Both statuses are shown in the history list.
  // TS-007 (T5, AC-TS007.6a / CRIT-2): 'ended_by_parent' is ALSO a terminal status —
  // widened here so a parent-ended session is NOT silently dropped from the student's
  // own session list (it renders as the non-tappable 'Ended early' marker, T11).
  const completedSessions = useMemo(() => {
    if (!sessions) return [];

    return sessions
      .filter(
        (session) =>
          session.status === 'completed' ||
          session.status === 'cancelled' ||
          session.status === 'ended_by_parent'
      )
      .sort((a, b) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [sessions]);

  return {
    sessions,
    activeSessions,
    completedSessions,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
